import React, { useEffect, useMemo } from 'react';

import { Button, message, Typography } from 'antd';
import moment from 'moment';
import { IoCheckmarkCircle, IoHandLeft, IoTimerOutline } from 'react-icons/io5';
import TMIcon from 'tm-icons-library';

import FeedAPI from '../../../../api/FeedAPI';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import SwipeableButton from '../../../../components/SwipeableButton/SwipeableButton';
import { useAppProvider } from '../../../../context/AppProvider';
import showAppError from '../../../../shared/error';
import { ICOLORS } from '../../../../shared/Styles';
import { IWebinar } from '../../../../types/feedTypes';
import { IUser } from '../../../../types/userTypes';
import Time from '../Time';

enum AttendanceStatus {
  SESSION_NOT_STARTED,
  SESSION_ACTIVE,
  LOADING,
  MARKED,
}

export interface UpcomingWorkshopCardProps {
  webinar: IWebinar;
  colors: ICOLORS;
  userDetails: IUser;
  onMeetingJoin: (webinar: IWebinar) => void;
  getMeetingJoinStatus?: (webinar: IWebinar) => string;
  isMeetingHost: (videoCall: IWebinar) => boolean;
}

const UpcomingWorkshopCard: React.FC<UpcomingWorkshopCardProps> = ({
  webinar,
  colors,
  userDetails,
  onMeetingJoin,
  getMeetingJoinStatus,
  isMeetingHost,
}) => {
  const { meetingJoinLoadingId } = useAppProvider();

  const isHostJoinLoading = meetingJoinLoadingId === webinar?._id;

  const [meetingStarted, setMeetingStarted] = React.useState(false);
  const [attendanceStatus, setAttendanceStatus] =
    React.useState<AttendanceStatus>(AttendanceStatus.SESSION_NOT_STARTED);

  const removeSessionTimer = React.useRef<NodeJS.Timeout>();

  const isJoinVideoCallAllowed = useMemo(
    () => webinar && getMeetingJoinStatus?.(webinar) === 'active',
    [webinar, getMeetingJoinStatus],
  );

  const timeDiffinMin = () => {
    const fromTime = webinar?.fromTime;

    if (fromTime) {
      const now = moment(new Date());
      const end = moment(fromTime);

      return moment.duration(end.diff(now)).asMinutes();
    }

    return 20;
  };

  // check if attendance session is active
  const fetchAttendanceStatus = async () => {
    if (!webinar) {
      return;
    }

    const errorMsg = 'Failed to check attendance status';
    setAttendanceStatus(AttendanceStatus.LOADING);

    try {
      const { data: resp } = await FeedAPI.checkWorkshopAttendanceStatus(
        webinar?._id,
      );
      if (resp.code === 0) {
        if (resp?.result) {
          setAttendanceStatus(AttendanceStatus.MARKED);
          removeSessionTimer.current = setTimeout(() => {
            setAttendanceStatus(AttendanceStatus.SESSION_NOT_STARTED);
          }, 30000);
        } else {
          setAttendanceStatus(AttendanceStatus.SESSION_ACTIVE);
        }
      } else {
        message.error(errorMsg);
        setAttendanceStatus(AttendanceStatus.SESSION_ACTIVE);
      }
    } catch (error) {
      message.error(errorMsg);
      setAttendanceStatus(AttendanceStatus.SESSION_ACTIVE);
    }
  };

  const onMarkAttendance = async () => {
    if (!webinar) {
      return;
    }
    try {
      setAttendanceStatus(AttendanceStatus.LOADING);
      const res = await FeedAPI.markAttendance(webinar._id);
      if (res.status === 200) {
        setAttendanceStatus(AttendanceStatus.MARKED);
        removeSessionTimer.current = setTimeout(() => {
          setAttendanceStatus(AttendanceStatus.SESSION_NOT_STARTED);
        }, 30000);
      } else {
        setAttendanceStatus(AttendanceStatus.SESSION_ACTIVE);
      }
    } catch (error) {
      setAttendanceStatus(AttendanceStatus.SESSION_ACTIVE);
      showAppError(error);
    }
  };

  useEffect(() => {
    if (webinar) {
      if (moment(webinar.fromTime).isSameOrAfter(moment())) {
        setTimeout(() => {
          setMeetingStarted(true);
        }, timeDiffinMin() * 60000);
      } else if (moment(webinar.fromTime).isBefore(moment())) {
        setMeetingStarted(true);
      } else {
        setMeetingStarted(false);
      }

      // check if attendance session is active
      if (webinar.currentAttendanceSession) {
        fetchAttendanceStatus();
      } else {
        setAttendanceStatus(AttendanceStatus.SESSION_NOT_STARTED);
      }
    }

    return () => {
      if (removeSessionTimer.current) {
        clearTimeout(removeSessionTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinar]);

  const onMeetingClick = () => {
    if (webinar) onMeetingJoin(webinar);
  };

  return (
    <div className="feed-card info">
      <div className="feed-card__icon-bar">
        <span className="feed-card__icon-bar__text">
          {moment(webinar.fromTime).isSame(moment(), 'd')
            ? 'TODAY'
            : moment(webinar.fromTime).format('ddd').toUpperCase()}
        </span>
      </div>

      <div
        className="feed-card__details-wrapper workshopCardFeed"
        style={{
          width: 'calc(100% - 36px)',
        }}>
        <div className="workshopCardDetails">
          <h5 className="workshopTitle">{webinar.title}</h5>

          <span className="time">
            {moment(webinar.fromTime).format('hh:mm A')} -{' '}
            {moment(webinar.toTime).format('hh:mm A')}
          </span>

          <span className="host" style={{ marginTop: 4 }}>
            By
            <strong className="hostName">{webinar.creatorDetails.name}</strong>
          </span>

          {webinar?.consultationFan ? (
            <Typography.Paragraph
              style={{
                marginTop: 4,
                marginBottom: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
              <ProfileItem
                title={webinar?.consultationFan?.name}
                avatar={webinar?.consultationFan?.profilePicUrl}
                avatarSize={20}
                titleStyle={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: colors.TEXT2,
                }}
              />
            </Typography.Paragraph>
          ) : null}
        </div>

        <div className="workshopCardAction">
          <span className="timer">
            <IoTimerOutline
              size={18}
              color={colors.ICON}
              style={{
                marginRight: '8px',
              }}
            />
            {meetingStarted ? 'Ends' : 'Starts'} In:{' '}
            <Time time={meetingStarted ? webinar.toTime : webinar.fromTime} />
          </span>

          <span onClick={!isJoinVideoCallAllowed ? onMeetingClick : undefined}>
            <Button
              className="siteBtn noStyle blueBtnV2 joinBtn"
              icon={
                isHostJoinLoading ? undefined : (
                  <TMIcon name="videocam" size={20} color={colors.LINK} />
                )
              }
              onClick={
                isJoinVideoCallAllowed && !isHostJoinLoading
                  ? onMeetingClick
                  : undefined
              }
              loading={isHostJoinLoading}
              disabled={!isJoinVideoCallAllowed || isHostJoinLoading}
              style={
                !isJoinVideoCallAllowed
                  ? {
                      pointerEvents: 'none',
                    }
                  : {}
              }>
              {isMeetingHost(webinar) ? 'Join as host' : 'Join'}
            </Button>
          </span>
        </div>

        {attendanceStatus !== AttendanceStatus.SESSION_NOT_STARTED &&
        webinar.creator !== userDetails.id &&
        userDetails.type !== 'creator_restricted' ? (
          <div className="workshopAttendanceContainer">
            {attendanceStatus === AttendanceStatus.MARKED ? (
              <div
                className="workshopAttendanceStatus"
                style={{
                  backgroundColor: `${colors.GREEN}30`,
                }}>
                {<IoCheckmarkCircle size={20} color={colors.GREEN} />}
                <span>Your attendance has been marked successfully!</span>
              </div>
            ) : (
              <SwipeableButton
                onSwipe={onMarkAttendance}
                isLoading={attendanceStatus === AttendanceStatus.LOADING}
                backgroundColor={`${colors.GREEN}30`}
                thumbColor={colors.GREEN}
                textColor={colors.TEXT3}
                thumbIcon={<IoHandLeft size={20} color={colors.BACKGROUND} />}
                text="Swipe to mark attendance"
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(UpcomingWorkshopCard);
